import { contains, noop } from 'underscore';
import React, { useCallback, useContext, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Button, Image } from '@fluentui/react-components';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { useResponsiveSize } from '@adscore/with-responsive-size';
import { useIntersectionObserver } from '@bingads-webui-campaign-react/pmax-landing-utils';

import { FeatureProgressItem } from './feature-progress-item';
import { FeatureConfig } from './feature-config';
import { getLandingPageFeatureStyles } from './landing-page-feature-styles';

const isSmallScreen = displayMode => contains(['s', 'm', 'l'], displayMode);
export function LandingPageFeature({ onClickSignUp, logger }) {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getLandingPageFeatureStyles);
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);

  const ref = useRef();
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);

  const scrollToNextSection = useCallback(() => {
    setCurrentFeatureIndex((index) => {
      const nextFeatureIndex = index + 1;
      return nextFeatureIndex >= FeatureConfig.length ? 0 : nextFeatureIndex;
    });
  }, []);

  const onScrollToView = useCallback(() => {
    logger.trace('LandingPageFeature scrolled into view');
  }, [logger]);

  const isFeatureSectionVisible = useIntersectionObserver({
    ref,
    onScrollToView,
  });

  useEffect(() => {
    if (isFeatureSectionVisible) {
      logger.trace(`[Feature] Feature ${FeatureConfig[currentFeatureIndex].key} selected`);
    }
  }, [currentFeatureIndex, isFeatureSectionVisible, logger]);

  return (
    <div ref={ref} className={classNames('landing-page-feature', classes.container)} role="region">
      <div className={classNames('inner-container', classes.innerContainer, classes[`innerContainer-${displayMode}`])}>
        <div className={classNames('feature-container', classes.featureContainer, classes[`featureContainer-${displayMode}`])}>
          <div className="feature-list">
            {FeatureConfig.map((feature, index) => (
              <React.Fragment key={feature.key}>
                <FeatureProgressItem
                  title={feature.title}
                  description={feature.description}
                  isActive={index === currentFeatureIndex}
                  scrollToNextSection={scrollToNextSection}
                  setActive={() => setCurrentFeatureIndex(index)}
                />
                {(isSmallScreen(displayMode) && index === currentFeatureIndex) &&
                  <div
                    className={classNames(
                      'animation-container',
                      classes.animationContainer,
                      classes[`animationContainer-${displayMode}`],
                      classes.animationContainerSmallScreen
                    )}
                  >
                    <Image
                      alt={`${FeatureConfig[currentFeatureIndex].title}`}
                      src={FeatureConfig[currentFeatureIndex].imageSrc}
                    />
                  </div>
                }
              </React.Fragment>
            ))}
          </div>
          <div className="get-started-button-container">
            <Button
              className="get-started-button"
              onClick={onClickSignUp}
              appearance="primary"
            >
              {i18n.getString(_TL_('Get started'))}
            </Button>
          </div>
        </div>
        {!isSmallScreen(displayMode) &&
          <div
            className={classNames(
              'animation-container',
              classes.animationContainer,
              classes[`animationContainer-${displayMode}`]
            )}
          >
            <Image
              alt={`${FeatureConfig[currentFeatureIndex].title}`}
              src={FeatureConfig[currentFeatureIndex].imageSrc}
            />
          </div>}
      </div>
    </div>
  );
}

LandingPageFeature.propTypes = {
  onClickSignUp: PropTypes.func.isRequired,
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
  }),
};

LandingPageFeature.defaultProps = {
  logger: {
    trace: noop,
  },
};
